import { useCallback, useEffect, useMemo, useState } from 'react';
import { Button, Modal, Form } from 'react-bootstrap';
import { createOrder, getAllAccounts } from '../services/api';
import RiskProfileModal from '../components/RiskProfileModal';
import { RISK_AMOUNT_OPTIONS, DEFAULT_RISK_AMOUNT, calculateBuyQuantity } from '../utils/riskProfile';

const getStandardSize = (account) => {
  const accountTotalValue = parseFloat(account.balance) || 0;
  return (account.auto_standard_size || !account.standard_size)
    ? Math.max(accountTotalValue / 10, 1000)
    : parseFloat(account.standard_size);
};

// sizingMode: 'fraction' sizes as a fraction of the account's standard size;
// 'risk' sizes from a dollar risk budget and the ticker's 21-day ATR.
const useQuickBuy = ({
  accounts = null,
  onOrderCreated,
  storageKey = 'quick_buy_default_fraction',
  loadAccounts = false,
  sizingMode = 'fraction'
} = {}) => {
  const [fetchedAccounts, setFetchedAccounts] = useState([]);
  const [showQuickBuyModal, setShowQuickBuyModal] = useState(false);
  const [quickBuyTicker, setQuickBuyTicker] = useState(null);
  const [quickBuyLoading, setQuickBuyLoading] = useState(false);
  const [showPositionSizeModal, setShowPositionSizeModal] = useState(false);
  const [selectedAccountForPosition, setSelectedAccountForPosition] = useState(null);
  const [showManualPriceModal, setShowManualPriceModal] = useState(false);
  const [manualPriceInput, setManualPriceInput] = useState('');
  const [manualPriceError, setManualPriceError] = useState('');
  const [pendingTargetSize, setPendingTargetSize] = useState(null);
  const RISK_AMOUNT_STORAGE_KEY = `${storageKey}_risk_amount`;
  const [riskAmount, setRiskAmount] = useState(() => {
    const parsed = Number(localStorage.getItem(RISK_AMOUNT_STORAGE_KEY));
    return RISK_AMOUNT_OPTIONS.includes(parsed) ? parsed : DEFAULT_RISK_AMOUNT;
  });
  const [atrPercent, setAtrPercent] = useState('');
  const [quickBuyDefaultFraction, setQuickBuyDefaultFraction] = useState(() => {
    const saved = localStorage.getItem(storageKey);
    const parsed = Number(saved);
    if (parsed === 1 || parsed === 0.5 || parsed === 0.25 || parsed === 0.125 || parsed === 0.0625 || parsed === 0.03125) {
      return parsed;
    }
    return 1;
  });
  const ORDER_TYPE_STORAGE_KEY = `${storageKey}_order_type`;
  const [orderType, setOrderType] = useState(() => {
    const saved = localStorage.getItem(ORDER_TYPE_STORAGE_KEY);
    if (saved === 'BUY_STOP') {
      return 'BUY_STOP';
    }
    return 'MARKET';
  });
  const [buyStopPrice, setBuyStopPrice] = useState('');
  const [buyStopError, setBuyStopError] = useState('');

  useEffect(() => {
    if (!loadAccounts || accounts) {
      return undefined;
    }
    let isMounted = true;
    getAllAccounts()
      .then((res) => {
        if (isMounted) {
          setFetchedAccounts(res.data || []);
        }
      })
      .catch(() => {
        if (isMounted) {
          setFetchedAccounts([]);
        }
      });
    return () => {
      isMounted = false;
    };
  }, [loadAccounts, accounts]);

  const effectiveAccounts = accounts ?? fetchedAccounts;

  const quickBuyAccounts = useMemo(() => {
    const eligible = (effectiveAccounts || []).filter(account => (parseFloat(account.balance) || 0) > 0);
    return eligible.sort(
      (a, b) => (parseFloat(b.balance) || 0) - (parseFloat(a.balance) || 0)
    );
  }, [effectiveAccounts]);

  const openQuickBuy = (ticker) => {
    setQuickBuyTicker(ticker);
    setBuyStopError('');
    setBuyStopPrice('');
    setShowQuickBuyModal(true);
  };

  const resetQuickBuy = useCallback(() => {
    setShowQuickBuyModal(false);
    setQuickBuyTicker(null);
    setShowPositionSizeModal(false);
    setSelectedAccountForPosition(null);
    setShowManualPriceModal(false);
    setManualPriceInput('');
    setManualPriceError('');
    setPendingTargetSize(null);
    setAtrPercent('');
    setBuyStopError('');
    setBuyStopPrice('');
  }, []);

  const entryPrice = useMemo(() => {
    const raw = orderType === 'BUY_STOP' ? buyStopPrice : quickBuyTicker?.price;
    const parsed = Number.parseFloat(raw);
    return Number.isFinite(parsed) && parsed > 0 ? parsed : null;
  }, [orderType, buyStopPrice, quickBuyTicker]);

  const handleAccountSelection = useCallback((account) => {
    if (orderType === 'BUY_STOP') {
      const parsedStop = Number.parseFloat(buyStopPrice);
      if (!Number.isFinite(parsedStop) || parsedStop <= 0) {
        setBuyStopError('Enter a valid buy stop price to continue.');
        return;
      }
    }
    setSelectedAccountForPosition(account);
    setShowQuickBuyModal(false);
    setShowPositionSizeModal(true);
  }, [orderType, buyStopPrice]);

  const handlePositionSizeCancel = useCallback(() => {
    setShowPositionSizeModal(false);
    setSelectedAccountForPosition(null);
    setShowQuickBuyModal(true);
  }, []);

  const submitQuickBuyOrder = useCallback(async (targetSize, priceOverride = null) => {
    if (!quickBuyTicker || !selectedAccountForPosition) return;

    setQuickBuyLoading(true);
    try {
      const isBuyStop = orderType === 'BUY_STOP';
      const resolvedPrice = (!isBuyStop && priceOverride != null) ? priceOverride : entryPrice;
      if (resolvedPrice == null) {
        if (isBuyStop) {
          setBuyStopError('Enter a valid buy stop price.');
          return;
        }
        setPendingTargetSize(targetSize);
        setManualPriceInput('');
        setManualPriceError('');
        setShowManualPriceModal(true);
        return;
      }

      const availableCash = parseFloat(selectedAccountForPosition.cash) || 0;
      const quantity = calculateBuyQuantity(targetSize, resolvedPrice, availableCash);

      if (!Number.isFinite(quantity) || quantity <= 0) {
        alert('Insufficient cash to buy any shares of this ticker.');
        return;
      }

      const orderData = {
        symbol: quickBuyTicker.symbol,
        quantity: quantity,
        action: 'BUY',
        type: isBuyStop ? 'STOP_LOSS' : 'LIMIT',
        price: resolvedPrice
      };

      await createOrder(selectedAccountForPosition.account_id, orderData);

      resetQuickBuy();
      if (onOrderCreated) {
        await onOrderCreated();
      }
    } catch (error) {
      console.error('Error creating quick buy order:', error);
      setManualPriceError('Error creating buy order. Please try again.');
    } finally {
      setQuickBuyLoading(false);
    }
  }, [quickBuyTicker, selectedAccountForPosition, orderType, entryPrice, onOrderCreated, resetQuickBuy]);

  const handlePositionSizeConfirm = useCallback(async (positionSizeFraction) => {
    await submitQuickBuyOrder(getStandardSize(selectedAccountForPosition) * positionSizeFraction);
  }, [submitQuickBuyOrder, selectedAccountForPosition]);

  const handleQuickBuyDefaultChange = useCallback((value) => {
    setQuickBuyDefaultFraction(value);
    localStorage.setItem(storageKey, String(value));
  }, [storageKey]);

  const handleRiskAmountChange = useCallback((value) => {
    setRiskAmount(value);
    localStorage.setItem(RISK_AMOUNT_STORAGE_KEY, String(value));
  }, [RISK_AMOUNT_STORAGE_KEY]);

  const handleOrderTypeChange = useCallback((value) => {
    setOrderType(value);
    localStorage.setItem(ORDER_TYPE_STORAGE_KEY, value);
    if (value !== 'BUY_STOP') {
      setBuyStopError('');
      setBuyStopPrice('');
    }
  }, [ORDER_TYPE_STORAGE_KEY]);

  const handleManualPriceSubmit = useCallback(async (event) => {
    event.preventDefault();
    const parsed = Number.parseFloat(manualPriceInput);
    if (!Number.isFinite(parsed) || parsed <= 0) {
      setManualPriceError('Enter a valid price greater than 0.');
      return;
    }
    const targetSize = pendingTargetSize;
    setShowManualPriceModal(false);
    setPendingTargetSize(null);
    await submitQuickBuyOrder(targetSize, parsed);
  }, [manualPriceInput, pendingTargetSize, submitQuickBuyOrder]);

  const quickBuyModals = useMemo(() => {
    const positionSummary = (
      <p className="mb-2">
        <strong>Account:</strong> {selectedAccountForPosition?.name}<br/>
        <strong>Ticker:</strong> {quickBuyTicker?.symbol}<br/>
        <strong>Price:</strong> ${quickBuyTicker?.price ? parseFloat(quickBuyTicker.price).toFixed(2) : 'N/A'}<br/>
        <strong>Available Cash:</strong> ${selectedAccountForPosition?.cash ? parseFloat(selectedAccountForPosition.cash).toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2}) : 'N/A'}
      </p>
    );
    return (
    <>
      <Modal show={showManualPriceModal} onHide={() => setShowManualPriceModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Enter Manual Price</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p className="text-muted mb-3">
            We couldn't find a live price for {quickBuyTicker?.symbol}. Enter a manual price to place this order.
          </p>
          <Form onSubmit={handleManualPriceSubmit}>
            <Form.Group controlId="quick-buy-manual-price">
              <Form.Label>Limit Price</Form.Label>
              <Form.Control
                type="number"
                min="0"
                step="any"
                value={manualPriceInput}
                onChange={(e) => {
                  setManualPriceInput(e.target.value);
                  if (manualPriceError) {
                    setManualPriceError('');
                  }
                }}
                required
              />
            </Form.Group>
            {manualPriceError && (
              <div className="text-danger mt-2">{manualPriceError}</div>
            )}
            <div className="d-flex justify-content-end gap-2 mt-4">
              <Button variant="secondary" onClick={resetQuickBuy}>
                Cancel
              </Button>
              <Button variant="primary" type="submit" disabled={quickBuyLoading}>
                {quickBuyLoading ? 'Creating...' : 'Create Order'}
              </Button>
            </div>
          </Form>
        </Modal.Body>
      </Modal>

      <Modal show={showQuickBuyModal} onHide={resetQuickBuy} centered size="lg">
        <Modal.Header closeButton>
          <div className="quick-buy-modal-header">
            <Modal.Title>Quick Buy {quickBuyTicker?.symbol}</Modal.Title>
            <div className="quick-buy-header-controls">
              <div className="d-flex gap-3 align-items-center">
                <Form.Check
                  type="radio"
                  id="quick-buy-order-market"
                  name="quick-buy-order-type"
                  label="Market"
                  value="MARKET"
                  checked={orderType === 'MARKET'}
                  onChange={() => handleOrderTypeChange('MARKET')}
                  disabled={quickBuyLoading}
                />
                <Form.Check
                  type="radio"
                  id="quick-buy-order-stop"
                  name="quick-buy-order-type"
                  label="Buy Stop"
                  value="BUY_STOP"
                  checked={orderType === 'BUY_STOP'}
                  onChange={() => handleOrderTypeChange('BUY_STOP')}
                  disabled={quickBuyLoading}
                />
              </div>
              <div className="d-flex align-items-center gap-2">
                <Form.Control
                  type="number"
                  min="0"
                  step="any"
                  placeholder="Stop price"
                  className="quick-buy-stop-input"
                  value={buyStopPrice}
                  onChange={(event) => {
                    setBuyStopPrice(event.target.value);
                    if (buyStopError) {
                      setBuyStopError('');
                    }
                  }}
                  disabled={quickBuyLoading || orderType !== 'BUY_STOP'}
                />
              </div>
            </div>
          </div>
        </Modal.Header>
        <Modal.Body>
          {orderType === 'BUY_STOP' && buyStopError && (
            <div className="text-danger mb-3">{buyStopError}</div>
          )}
          <div className="mb-3">
            <Form.Label>Select Account:</Form.Label>
            <div className="row g-2">
              {quickBuyAccounts.length === 0 ? (
                <div className="col-12">
                  <div className="alert alert-info mb-0">
                    No funded accounts available for quick buy.
                  </div>
                </div>
              ) : (
                quickBuyAccounts.slice(0, 16).map(account => (
                  <div key={account.account_id} className="col-4">
                    <div
                      className="card h-100 border-light"
                      style={{
                        cursor: 'pointer',
                        transition: 'all 0.2s ease',
                        opacity: quickBuyLoading ? 0.6 : 1,
                        boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
                        minHeight: '80px'
                      }}
                      onClick={() => {
                        if (quickBuyLoading) return;
                        handleAccountSelection(account);
                      }}
                      onMouseEnter={(e) => {
                        if (!quickBuyLoading) {
                          e.currentTarget.style.transform = 'translateY(-2px)';
                          e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.15)';
                        }
                      }}
                      onMouseLeave={(e) => {
                        if (!quickBuyLoading) {
                          e.currentTarget.style.transform = 'translateY(0)';
                          e.currentTarget.style.boxShadow = '0 1px 3px rgba(0,0,0,0.1)';
                        }
                      }}
                    >
                      <div className="card-body p-1 position-relative">
                        <div className="mb-1 text-center">
                          <h6 className="card-title mb-0" style={{ fontSize: '1.3rem', fontWeight: 'bold', lineHeight: '1.1' }}>
                            {account.name.length > 8 ? account.name.substring(0, 8) + '...' : account.name}
                          </h6>
                        </div>
                        <div className="row g-0">
                          <div className="col-6 text-center">
                            <div className="text-success fw-bold" style={{ fontSize: '1.3rem', lineHeight: '1' }}>
                              ${(parseFloat(account.balance || 0) / 1000).toFixed(0)}k
                            </div>
                            <small className="text-muted" style={{ fontSize: '1.1rem' }}>Bal</small>
                          </div>
                          <div className="col-6 text-center">
                            <div className="text-primary fw-semibold" style={{ fontSize: '1.2rem', lineHeight: '1' }}>
                              ${(parseFloat(account.cash || 0) / 1000).toFixed(0)}k
                            </div>
                            <small className="text-muted" style={{ fontSize: '1rem' }}>Cash</small>
                          </div>
                        </div>
                        {account.balance > 0 && (() => {
                          const investedPct = Math.max(0, Math.min(100, 100 - (((account.cash || 0) / account.balance) * 100)));
                          const cashPct = Math.max(0, Math.min(100, (((account.cash || 0) / account.balance) * 100)));
                          const barStyle = { width: `${investedPct}%` };
                          return (
                            <div
                              className="mt-1 account-invested-progress"
                              style={{ height: '24px', position: 'relative' }}
                              title={`Invested: ${Math.round(investedPct)}% | Cash: ${Math.round(cashPct)}%`}
                            >
                              <div
                                className="account-invested-progress-bar"
                                style={barStyle}
                              />
                              <div
                                className="position-absolute top-0 start-0 w-100 h-100"
                                style={{ pointerEvents: 'none', display: 'flex' }}
                              >
                                <div style={{ width: `${investedPct}%`, position: 'relative' }}>
                                  {investedPct > 20 && (
                                    <span
                                      className="d-inline-block"
                                      style={{ position: 'absolute', right: 6, top: '50%', transform: 'translateY(-50%)', color: '#fff', fontWeight: 700, fontSize: '0.8rem', textAlign: 'right' }}
                                    >
                                      {`${Math.round(investedPct)}%`}
                                    </span>
                                  )}
                                </div>
                                <div style={{ width: `${cashPct}%` }} />
                              </div>
                            </div>
                          );
                        })()}
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
          {quickBuyLoading && (
            <div className="text-center mt-3">
              <div className="spinner-border text-primary" role="status">
                <span className="visually-hidden">Creating order...</span>
              </div>
              <div className="mt-2">Creating order...</div>
            </div>
          )}
        </Modal.Body>
      </Modal>

      {sizingMode === 'risk' ? (
        <RiskProfileModal
          show={showPositionSizeModal}
          onBack={handlePositionSizeCancel}
          symbol={quickBuyTicker?.symbol}
          summary={positionSummary}
          entryPrice={entryPrice}
          availableCash={selectedAccountForPosition?.cash}
          riskAmount={riskAmount}
          onRiskAmountChange={handleRiskAmountChange}
          atrPercent={atrPercent}
          onAtrPercentChange={setAtrPercent}
          loading={quickBuyLoading}
          onConfirm={submitQuickBuyOrder}
        />
      ) : (
      <Modal show={showPositionSizeModal} onHide={handlePositionSizeCancel} centered>
        <Modal.Header closeButton>
          <Modal.Title>Select Position Size for {quickBuyTicker?.symbol}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="mb-3">{positionSummary}</div>
          <Form.Group className="mb-3" controlId="quick-buy-default-position-size">
            <Form.Label>Default position size</Form.Label>
            <Form.Select
              value={quickBuyDefaultFraction}
              onChange={(event) => handleQuickBuyDefaultChange(Number(event.target.value))}
              disabled={quickBuyLoading}
            >
              <option value={1}>Full Position (1X)</option>
              <option value={0.5}>Half Position (1/2X)</option>
              <option value={0.25}>Quarter Position (1/4X)</option>
              <option value={0.125}>Eighth Position (1/8X)</option>
              <option value={0.0625}>Sixteenth Position (1/16X)</option>
              <option value={0.03125}>Thirty-Second Position (1/32X)</option>
            </Form.Select>
          </Form.Group>
          <div className="d-grid gap-3">
            <Button
              variant={quickBuyDefaultFraction === 1 ? 'primary' : 'success'}
              size="lg"
              onClick={() => handlePositionSizeConfirm(1)}
              disabled={quickBuyLoading}
              style={{ fontSize: '1.2rem', padding: '1rem' }}
            >
              {quickBuyLoading ? (
                <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true" />
              ) : null}
              Full Position (1X){quickBuyDefaultFraction === 1 ? ' - Default' : ''}
            </Button>
            <Button
              variant={quickBuyDefaultFraction === 0.5 ? 'primary' : 'success'}
              size="lg"
              onClick={() => handlePositionSizeConfirm(0.5)}
              disabled={quickBuyLoading}
              style={{ fontSize: '1.2rem', padding: '1rem' }}
            >
              {quickBuyLoading ? (
                <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true" />
              ) : null}
              Half Position (1/2X){quickBuyDefaultFraction === 0.5 ? ' - Default' : ''}
            </Button>
            <Button
              variant={quickBuyDefaultFraction === 0.25 ? 'primary' : 'warning'}
              size="lg"
              onClick={() => handlePositionSizeConfirm(0.25)}
              disabled={quickBuyLoading}
              style={{ fontSize: '1.2rem', padding: '1rem' }}
            >
              {quickBuyLoading ? (
                <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true" />
              ) : null}
              Quarter Position (1/4X){quickBuyDefaultFraction === 0.25 ? ' - Default' : ''}
            </Button>
            <Button
              variant={quickBuyDefaultFraction === 0.125 ? 'primary' : 'warning'}
              size="lg"
              onClick={() => handlePositionSizeConfirm(0.125)}
              disabled={quickBuyLoading}
              style={{ fontSize: '1.2rem', padding: '1rem' }}
            >
              {quickBuyLoading ? (
                <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true" />
              ) : null}
              Eighth Position (1/8X){quickBuyDefaultFraction === 0.125 ? ' - Default' : ''}
            </Button>
            <Button
              variant={quickBuyDefaultFraction === 0.0625 ? 'primary' : 'warning'}
              size="lg"
              onClick={() => handlePositionSizeConfirm(0.0625)}
              disabled={quickBuyLoading}
              style={{ fontSize: '1.2rem', padding: '1rem' }}
            >
              {quickBuyLoading ? (
                <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true" />
              ) : null}
              Sixteenth Position (1/16X){quickBuyDefaultFraction === 0.0625 ? ' - Default' : ''}
            </Button>
            <Button
              variant={quickBuyDefaultFraction === 0.03125 ? 'primary' : 'warning'}
              size="lg"
              onClick={() => handlePositionSizeConfirm(0.03125)}
              disabled={quickBuyLoading}
              style={{ fontSize: '1.2rem', padding: '1rem' }}
            >
              {quickBuyLoading ? (
                <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true" />
              ) : null}
              Thirty-Second Position (1/32X){quickBuyDefaultFraction === 0.03125 ? ' - Default' : ''}
            </Button>
          </div>
          {quickBuyLoading && (
            <div className="text-center mt-3">
              <div className="mt-2">Creating order...</div>
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={handlePositionSizeCancel} disabled={quickBuyLoading}>
            Back
          </Button>
        </Modal.Footer>
      </Modal>
      )}
    </>
    );
  }, [
    buyStopError,
    buyStopPrice,
    orderType,
    quickBuyTicker,
    quickBuyAccounts,
    quickBuyDefaultFraction,
    quickBuyLoading,
    manualPriceError,
    manualPriceInput,
    selectedAccountForPosition,
    showManualPriceModal,
    showPositionSizeModal,
    showQuickBuyModal,
    sizingMode,
    entryPrice,
    riskAmount,
    atrPercent,
    resetQuickBuy,
    handlePositionSizeCancel,
    handleAccountSelection,
    handlePositionSizeConfirm,
    handleQuickBuyDefaultChange,
    handleRiskAmountChange,
    submitQuickBuyOrder,
    handleOrderTypeChange,
    handleManualPriceSubmit
  ]);

  return {
    openQuickBuy,
    quickBuyModals
  };
};

export default useQuickBuy;
